// Core helper methods for TheoryCoach
// Extracted to reduce main file size and prevent Cursor crashes

import { TheoryCoachContext } from './theoryCoach.types';

/**
 * Get language name
 */
export const getLanguageName = (language: string): string => {
  const names: Record<string, string> = {
    en: 'English',
    nl: 'Dutch',
    ar: 'Arabic'
  };
  return names[language] || 'English';
};

/**
 * Fallback response - Context-aware based on user message
 * Provides direct answers, not questions, when user has given context
 */
export const getFallbackResponse = (context: TheoryCoachContext): string => {
  const userMessage = (context.userMessage || '').toLowerCase().trim();
  const language = context.language || 'en';
  const currentQuestion = context.currentQuestion;
  const explanation = currentQuestion?.explanation || '';
  const conciseExplanation = explanation.split(/[.!?]/)[0]?.trim() || '';
  
  // Handle specific user messages with DIRECT ANSWERS (not questions)
  // "I misread/misred the question" → Simple encouragement to read carefully (misred = common STT typo)
  if (userMessage.includes('misread') || userMessage.includes('misred') ||
      userMessage.includes('misread the question') || userMessage.includes('misread the light') || userMessage.includes('misread the sign')) {
    // Simple, encouraging response
    if (language === 'nl') {
      return 'Neem je tijd en lees zorgvuldig.';
    }
    if (language === 'ar') {
      return 'خذ وقتك واقرأ بعناية.';
    }
    return 'Take your time, read carefully.';
  }
  
  if (userMessage.includes('guessed') || userMessage.includes('guess')) {
    // NEW DESIGN: "I guessed" → Provide SHORT, CLEAR, PASS-READY explanation (1 sentence max)
    // Extract the first sentence or key rule from explanation
    const ruleExplanation = conciseExplanation || explanation.split(/[.!?]/)[0]?.trim() || explanation.substring(0, 100).trim();
    if (language === 'nl') {
      return ruleExplanation || 'De regel voor deze vraag is belangrijk.';
    }
    if (language === 'ar') {
      return ruleExplanation || 'القاعدة لهذا السؤال مهمة.';
    }
    return ruleExplanation || 'The rule for this question is important.';
  }

  // "I had no idea" / "no idea" (round 1 card) → key rule, same as guessed
  if (userMessage.includes('no idea') || userMessage.includes('had no idea') ||
      (language === 'nl' && (userMessage.includes('geen idee') || userMessage.includes('weet niet'))) ||
      (language === 'ar' && (userMessage.includes('لا فكرة') || userMessage.includes('لا أعرف')))) {
    const ruleExplanation = conciseExplanation || explanation.split(/[.!?]/)[0]?.trim() || explanation.substring(0, 100).trim();
    if (language === 'nl') {
      return ruleExplanation || 'De regel voor deze vraag is belangrijk.';
    }
    if (language === 'ar') {
      return ruleExplanation || 'القاعدة لهذا السؤال مهمة.';
    }
    return ruleExplanation || 'The rule for this question is important.';
  }

  // Handle "Explain further" - use same first sentence as key rule (consistency)
  if (userMessage.includes('explain further') || userMessage.includes('explain more') ||
      (language === 'nl' && (userMessage.includes('leg uit') || userMessage.includes('meer uitleg') || userMessage.includes('leg verder'))) ||
      (language === 'ar' && (userMessage.includes('اشرح أكثر') || userMessage.includes('مزيد من الشرح')))) {
    // Use same logic as getConciseExplanationText for consistency
    // Key rule, take-home, and "explain further" should all show the same sentence
    const clean = explanation.replace(/\s+/g, ' ').trim();
    const keyRule = clean.match(/[^.!?؟]+[.!?؟]*/)?.[0]?.trim() || conciseExplanation || explanation;
    
    if (language === 'nl') {
      return keyRule || 'De regel voor deze vraag is belangrijk.';
    }
    if (language === 'ar') {
      return keyRule || 'القاعدة لهذا السؤال مهمة.';
    }
    return keyRule || 'The rule for this question is important.';
  }
  
  if (userMessage.includes('confused') || userMessage.includes('mixed up')) {
    // Provide direct clarification
    if (language === 'nl') {
      return conciseExplanation
        ? `Deze vraag gaat over: ${conciseExplanation.substring(0, 80)}.`
        : 'Deze vraag heeft een specifieke regel. Lees de uitleg hierboven om het onderscheid te maken.';
    }
    if (language === 'ar') {
      return conciseExplanation
        ? `هذا السؤال يتعلق بـ: ${conciseExplanation.substring(0, 80)}.`
        : 'هذا السؤال له قاعدة محددة. اقرأ الشرح أعلاه للتمييز.';
    }
    return conciseExplanation
      ? `This question is about: ${conciseExplanation.substring(0, 80)}.`
      : 'This question has a specific rule. Read the explanation above to distinguish it.';
  }
  
  // Generic fallback: go straight to move-on (no "You're doing well. Ready to continue?" etc.)
  if (language === 'nl') return 'Zeg doorgaan of tik wanneer je klaar bent.';
  if (language === 'ar') return 'قل متابعة أو اضغط عندما تكون جاهزاً.';
  return "Say continue or tap when you're ready.";
};

/**
 * Check if should guide to focus area
 */
export const shouldGuideToFocusArea = (context: TheoryCoachContext): boolean => {
  // Guide to focus area:
  // - After completing a test
  // - Between questions (every 3-5 questions)
  // - When user asks about what to practice
  if (context.questionNumber && context.questionNumber % 3 === 0) {
    return true;
  }
  if (context.userMessage && 
      (context.userMessage.toLowerCase().includes('what') || 
       context.userMessage.toLowerCase().includes('practice') ||
       context.userMessage.toLowerCase().includes('focus'))) {
    return true;
  }
  return false;
};

/**
 * Get transition message between questions - MINIMAL (only when useful)
 */
export const getTransitionMessage = (_context: TheoryCoachContext): string => {
  // Minimal, short, neutral transitions only when useful
  // Most of the time, just move to next question silently
  return ''; // Return empty string to skip transition
};

/**
 * Guide to focus area
 */
export const guideToFocusArea = (focusArea: any, context: TheoryCoachContext): string => {
  const { getFocusAreaTemplates } = require('./theoryCoach.templates');
  const areaName = focusArea.testName || focusArea.testId || 'this area';
  const templates = getFocusAreaTemplates(context.language);
  const template = templates[Math.floor(Math.random() * templates.length)];
  return template.replace('[Focus Area]', areaName);
};

/**
 * Get continue message
 */
export const getContinueMessage = (context: TheoryCoachContext): string => {
  const { getContinueTemplates } = require('./theoryCoach.templates');
  const templates = getContinueTemplates(context.language);
  return templates[Math.floor(Math.random() * templates.length)];
};

/**
 * Generate answer from knowledge base
 */
export const generateAnswerFromKnowledge = (knowledgeItem: any, context: TheoryCoachContext): string => {
  const { summarizeExplanation } = require('./theoryCoach.templates');
  const explanation = knowledgeItem.explanation || '';
  const briefExplanation = summarizeExplanation(explanation, context.language);
  
  const templates: Record<string, string[]> = {
    en: [
      `${briefExplanation} Does that help? Ready to continue?`,
      `${briefExplanation} Let's keep practicing.`,
      `${briefExplanation} Ready for the next question?`
    ],
    nl: [
      `${briefExplanation} Helpt dat? Klaar om door te gaan?`,
      `${briefExplanation} Laten we doorgaan met oefenen.`,
      `${briefExplanation} Klaar voor de volgende vraag?`
    ],
    ar: [
      `${briefExplanation} هل هذا يساعد؟ هل أنت مستعد للمتابعة؟`,
      `${briefExplanation} دعنا نستمر في التدريب.`,
      `${briefExplanation} هل أنت مستعد للسؤال التالي؟`
    ]
  };
  
  const langTemplates = templates[context.language] || templates.en;
  return langTemplates[Math.floor(Math.random() * langTemplates.length)];
};
